import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and bust browser cache
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Prevent browser from serving stale cached API responses
    if (config.method === 'get') {
      config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      config.headers['Pragma'] = 'no-cache';
      config.params = { ...config.params, _t: Date.now() };
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
};

// Employee APIs
export const employeeAPI = {
  getAll: (company) => api.get('/employees', { params: { company } }),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  deactivate: (id) => api.patch(`/employees/${id}/deactivate`),
  activate: (id) => api.patch(`/employees/${id}/activate`),
  offboard: (id, data) => api.post(`/employees/${id}/offboard`, data),
  resendInvite: (id, data) => api.post(`/employees/${id}/resend-invite`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  downloadPdf: (id) => api.get(`/employees/${id}/download-pdf`, { responseType: 'blob' }),
};

// Upload API
export const uploadAPI = {
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadDocument: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Stats API
export const statsAPI = {
  getDashboard: () => api.get('/stats/dashboard'),
};

// Profile APIs
export const profileAPI = {
  get: () => api.get('/profile'),
  update: (data) => api.put('/profile', data),
};

// Dashboard APIs
export const dashboardAPI = {
  getAdmin: () => api.get('/dashboard/admin'),
  getEmployee: () => api.get('/dashboard/employee'),
  clearData: (data) => api.post('/admin/clear-all-data', data),
};

// Notification APIs
export const notificationAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
};

// Attendance APIs
export const attendanceAPI = {
  // Employee endpoints
  markAttendance: (data) => api.post('/attendance/mark', data),
  checkout: (data) => api.post('/attendance/checkout', data),
  getTodayStatus: () => api.get('/attendance/today'),
  getCalendar: (year, month) => api.get(`/attendance/calendar/${year}/${month}`),
  
  // Admin endpoints
  getDailyView: (date) => api.get('/attendance/admin/daily', { params: { attendance_date: date } }),
  getMatrix: (year, month) => api.get(`/attendance/admin/matrix/${year}/${month}`),
  adminUpdate: (data) => api.post('/attendance/admin/update', data),
};

// Office Locations APIs
export const officeLocationAPI = {
  getAll: () => api.get('/office-locations'),
  create: (data) => api.post('/office-locations', data),
  delete: (id) => api.delete(`/office-locations/${id}`),
};

// Holiday APIs
export const holidayAPI = {
  getHolidays: (year) => api.get(`/holidays/${year}`),
  createHoliday: (data) => api.post('/holidays', data),
  deleteHoliday: (id) => api.delete(`/holidays/${id}`),
  uploadCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/holidays/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Leave Type APIs
export const leaveTypeAPI = {
  getAll: () => api.get('/leave-types'),
  getTypes: () => api.get('/leave-types'),  // Alias for getAll
  create: (data) => api.post('/leave-types', data),
  update: (id, data) => api.put(`/leave-types/${id}`, data),
  delete: (id) => api.delete(`/leave-types/${id}`),
  seedDefaults: () => api.post('/leave-types/seed-defaults'),
};

// Leave Balance APIs
export const leaveBalanceAPI = {
  getMy: () => api.get('/leave-balances'),
  getForEmployee: (employeeId) => api.get('/leave-balances', { params: { employee_id: employeeId } }),
  getAll: () => api.get('/leave-balances/all'),
  adjust: (data) => api.post('/leave-balances/adjust', data),
  creditCompOff: (data) => api.post('/leave-balances/comp-off', data),
  annualReset: (carryForward = true) => api.post('/leave-balances/annual-reset', null, { params: { carry_forward_earned: carryForward } }),
};

// Leave Application APIs
export const leaveApplicationAPI = {
  apply: (data) => api.post('/leave-applications', data),
  getMy: () => api.get('/leave-applications/my'),
  getPending: () => api.get('/leave-applications/pending'),
  update: (id, data) => api.put(`/leave-applications/${id}`, data),
  edit: (id, data) => api.put(`/leave-applications/${id}/edit`, data),
  delete: (id) => api.delete(`/leave-applications/${id}`),
  getCalendar: (year, month) => api.get(`/leave-applications/calendar/${year}/${month}`),
  getReport: (employeeId, year) => api.get(`/leave-report/${employeeId}`, { params: { year } }),
};

// Office Expense APIs (Track 1 - Admin)
export const officeExpenseAPI = {
  getCategories: () => api.get('/expenses/office/categories'),
  createCategory: (data) => api.post('/expenses/office/categories', data),
  updateCategory: (id, data) => api.put(`/expenses/office/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/expenses/office/categories/${id}`),
  create: (data) => api.post('/expenses/office', data),
  getAll: (params) => api.get('/expenses/office', { params }),
  update: (id, data) => api.put(`/expenses/office/${id}`, data),
  delete: (id) => api.delete(`/expenses/office/${id}`),
};

// Reimbursement APIs (Track 2)
export const reimbursementAPI = {
  getCategories: () => api.get('/reimbursements/categories'),
  submit: (data) => api.post('/reimbursements', data),
  getAll: (params) => api.get('/reimbursements', { params }),
  getMy: () => api.get('/reimbursements/my'),
  getById: (id) => api.get(`/reimbursements/${id}`),
  update: (id, data) => api.put(`/reimbursements/${id}`, data),
  delete: (id) => api.delete(`/reimbursements/${id}`),
  review: (id, data) => api.patch(`/reimbursements/${id}/review`, data),
  markPaid: (id, data) => api.patch(`/reimbursements/${id}/paid`, data),
  getSummary: () => api.get('/reimbursements/summary'),
};

// Reports APIs
export const reportsAPI = {
  attendance: (params) => api.get('/reports/attendance', { params }),
  leave: (params) => api.get('/reports/leave', { params }),
  officeExpenses: (params) => api.get('/reports/office-expenses', { params }),
  reimbursements: (params) => api.get('/reports/reimbursements', { params }),
  payroll: (params) => api.get('/reports/payroll', { params }),
};

// Policy APIs
export const policyAPI = {
  getAll: () => api.get('/policies'),
  upload: (formData) => api.post('/policies/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/policies/${id}`),
};

// Evaluation APIs
export const evaluationAPI = {
  getCriteria: () => api.get('/evaluations/criteria'),
  updateCriteria: (data) => api.put('/evaluations/criteria', data),
  getAll: (params) => api.get('/evaluations', { params }),
  getById: (id) => api.get(`/evaluations/${id}`),
  create: (data) => api.post('/evaluations', data),
  update: (id, data) => api.put(`/evaluations/${id}`, data),
  delete: (id) => api.delete(`/evaluations/${id}`),
  getHistory: (employeeId) => api.get(`/evaluations/employee/${employeeId}/history`),
  getSummary: (year) => api.get(`/evaluations/summary/${year}`),
  exportExcel: (year) => api.get(`/evaluations/export/${year}/excel`, { responseType: 'blob' }),
  exportPdf: (year) => api.get(`/evaluations/export/${year}/pdf`, { responseType: 'blob' }),
};

// Probation APIs
export const probationAPI = {
  getUpcoming: () => api.get('/probation/upcoming'),
  triggerCheck: () => api.post('/probation/check'),
  convertToPermanent: (employeeId) => api.post(`/probation/convert/${employeeId}`),
};

// Push Notification APIs
export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-key'),
  subscribe: (subscription) => api.post('/push/subscribe', subscription),
  unsubscribe: (subscription) => api.post('/push/unsubscribe', subscription),
};

// Chat / Messaging APIs
export const chatAPI = {
  getConversations: () => api.get('/chat/conversations'),
  getConversation: (id) => api.get(`/chat/conversations/${id}`),
  createConversation: (data) => api.post('/chat/conversations', data),
  updateConversation: (id, data) => api.put(`/chat/conversations/${id}`, data),
  deleteConversation: (id) => api.delete(`/chat/conversations/${id}`),
  getMessages: (convId, skip = 0, limit = 50) => api.get(`/chat/conversations/${convId}/messages`, { params: { skip, limit } }),
  sendMessage: (convId, data) => api.post(`/chat/conversations/${convId}/messages`, data),
  uploadFile: (convId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/chat/conversations/${convId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  initChunkedUpload: (convId, data) => api.post(`/chat/conversations/${convId}/upload/init`, data),
  uploadChunk: (convId, uploadId, chunkIndex, blob) => {
    const formData = new FormData();
    formData.append('file', blob, `chunk_${chunkIndex}`);
    return api.post(`/chat/conversations/${convId}/upload/chunk/${uploadId}/${chunkIndex}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  completeChunkedUpload: (convId, uploadId) => api.post(`/chat/conversations/${convId}/upload/complete/${uploadId}`),
  getChatUsers: () => api.get('/chat/chat-users'),
};

export default api;
